package com.snakesworld.game.network

/**
 * Blocks nicknames that contain common profanity, including simple leetspeak and
 * a small set of terms in the languages the app ships (English, Spanish, French,
 * Portuguese, Japanese, Korean, and Chinese).
 *
 * Matching is case-insensitive. Latin terms of 3 letters or fewer must match a
 * whole token so words like ASSASSIN stay allowed. Longer terms and non-Latin
 * terms match inside the compacted name.
 *
 * The same check is used when a name is entered and when any name is shown.
 */
object NicknameValidator {
    const val MAX_LENGTH: Int = 16

    /** Shown in place of a blank or rejected nickname. */
    const val FALLBACK_NAME: String = "PLAYER"

    private val allowedCharacters = Regex("^[\\p{L}\\p{N} _\\-]+$")

    /**
     * Latin stems that are real words inside longer clean names (REPUTATION, UNIQUE).
     * They still reject when the whole token is the slur.
     */
    private val wholeTokenOnly = setOf("PUTA", "PUTO", "NIQUE", "MERDA", "PORRA")

    private val blocked = setOf(
        // English
        "ANAL", "ANUS", "ARSE", "ASS", "ASSHOLE", "BALLS", "BASTARD", "BITCH",
        "BLOWJOB", "BOLLOCK", "BONER", "BOOB", "BUGGER", "BULLSHIT", "BUTT",
        "BUTTHOLE", "CLIT", "COCK", "COON", "CRAP", "CUM", "CUNT", "DAMN",
        "DICK", "DILDO", "DYKE", "FAG", "FAGGOT", "FELTCH", "FEMDOM", "FUCK",
        "FUK", "GODDAMN", "HANDJOB", "HOMO", "HOOKER", "HORNY", "JIZZ", "KIKE",
        "LABIA", "LESBO", "MOTHERFUCKER", "MUFF", "NIGGA", "NIGGER", "ORGY",
        "PENIS", "PISS", "POON", "PORN", "PRICK", "PUBE", "PUSSY", "QUEER",
        "RAPE", "RAPIST", "RECTUM", "RETARD", "SCROTUM", "SEX", "SHIT", "SLUT",
        "SMEGMA", "SPUNK", "SUCK", "TIT", "TITS", "TURD", "TWAT", "VAGINA",
        "VULVA", "WANK", "WHORE", "WTF",
        // Spanish
        "CABRON", "CABRÓN", "CHINGAR", "COÑO", "GILIPOLLAS", "JODER", "MARICON",
        "MARICÓN", "MIERDA", "PUTA", "PUTO",
        // French
        "CONNARD", "ENCULE", "ENCULÉ", "MERDE", "NIQUE", "PUTAIN", "SALOPE",
        // Portuguese
        "BUCETA", "CARALHO", "FODER", "MERDA", "PORRA", "PUTA",
        // Japanese
        "くそ", "クソ", "しね", "死ね", "くそったれ",
        // Korean
        "씨발", "시발", "병신", "좆",
        // Chinese (simplified and traditional)
        "傻逼", "傻屄", "操你", "操你妈", "他媽的", "妈的", "去死",
    )

    /** True when [nickname] is non-blank, within length, and does not contain blocked terms. */
    fun isAllowed(nickname: String): Boolean {
        val trimmed = nickname.trim()
        if (trimmed.isEmpty() || trimmed.length > MAX_LENGTH) return false
        if (!allowedCharacters.matches(trimmed)) return false
        val upper = trimmed.uppercase()
        val normalized = normalizeLeet(upper)
        val compacted = normalized.filter { it.isLetterOrDigit() }
        val tokens = normalized.split(Regex("[^\\p{L}\\p{N}]+")).filter { it.isNotEmpty() }
        val candidates = tokens + listOf(compacted)
        for (word in blocked) {
            val wordKey = word.uppercase()
            if (candidates.any { it == wordKey || it.contains(wordKey) }) {
                if (isShortLatinStem(wordKey) || wordKey in wholeTokenOnly) {
                    if (tokens.any { it == wordKey } || compacted == wordKey) return false
                    continue
                }
                return false
            }
        }
        return true
    }

    /**
     * Name safe to draw for any player. Rejected or blank names become [FALLBACK_NAME].
     */
    fun forDisplay(raw: String): String {
        val trimmed = raw.trim()
        if (!isAllowed(trimmed)) return FALLBACK_NAME
        return trimmed
    }

    private fun isShortLatinStem(word: String): Boolean =
        word.length <= 3 && word.all { it in 'A'..'Z' || it in '0'..'9' }

    private fun normalizeLeet(value: String): String =
        buildString(value.length) {
            for (ch in value) {
                append(
                    when (ch) {
                        '0' -> 'O'
                        '1' -> 'I'
                        '3' -> 'E'
                        '4' -> 'A'
                        '5' -> 'S'
                        '7' -> 'T'
                        '@' -> 'A'
                        '$' -> 'S'
                        else -> ch
                    }
                )
            }
        }
}
